import { DatabaseIdAlphabet } from '../../utility/database-id-generator.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { SerializationException } from '../../exceptions/serialization-exception.js';
import { BinaryBlobFactory, DataModelFactory } from './data-model-factory.js';
import {
  BinaryBlob, DataModel, SerializationContext, SerializationStorage, SerializedData,
  UnknownDataModelTypePolicy,
} from './data-model.js';
import { Dayjs } from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';
import { TimeZone } from '../../time/timezone.js';

class SerializationContextImpl implements SerializationContext {
  public readonly storage: SerializationStorage = new SerializationStorage();

  get unknownTypePolicy(): UnknownDataModelTypePolicy {
    if(isNullOrUndefined(DataModelFactory.instance)) {
      throw new SerializationException('Data model factory is not initialized.');
    }

    return DataModelFactory.instance.unknownTypePolicy;
  }

  generateUniqueId(options?: {length?: number, alphabet?: DatabaseIdAlphabet}): string {
    if(isNullOrUndefined(DataModelFactory.instance)) {
      throw new SerializationException('Data model factory is not initialized.');
    }

    return DataModelFactory.instance.databaseIdGenerator.generate(options);
  }

  isSerializedData(map: SerializedData, typeId?: string): boolean {
    if(isNullOrUndefined(DataModelFactory.instance)) {
      throw new SerializationException('Data model factory is not initialized.');
    }

    return DataModelFactory.instance.isSerializedData(map, typeId);
  }

  toBinaryBlob(bytes: Uint8Array): BinaryBlob {
    if(isNullOrUndefined(BinaryBlobFactory.instance)) {
      throw new SerializationException('Binary blob factory is not initialized.');
    }

    return BinaryBlobFactory.instance.toBinaryBlob(bytes);
  }

  fromBinaryBlob(blob: BinaryBlob): Uint8Array {
    if(isNullOrUndefined(BinaryBlobFactory.instance)) {
      throw new SerializationException('Binary blob factory is not initialized.');
    }

    return BinaryBlobFactory.instance.fromBinaryBlob(blob);
  }

  toBase64(bytes: Uint8Array): string {
    if(isNullOrUndefined(BinaryBlobFactory.instance)) {
      throw new SerializationException('Binary blob factory is not initialized.');
    }

    return BinaryBlobFactory.instance.toBase64(bytes);
  }

  fromBase64(str: string): Uint8Array {
    if(isNullOrUndefined(BinaryBlobFactory.instance)) {
      throw new SerializationException('Binary blob factory is not initialized.');
    }

    return BinaryBlobFactory.instance.fromBase64(str);
  }

  toData<T extends DataModel>(model: T): SerializedData {
    if(isNullOrUndefined(DataModelFactory.instance)) {
      throw new SerializationException('Data model factory is not initialized.');
    }

    return DataModelFactory.instance.toData(this, model);
  }

  toDataOrNull<T extends DataModel>(model: T | null): SerializedData | null {
    return isNullOrUndefined(model) ? null : this.toData(model);
  }

  fromData<T extends DataModel>(map: SerializedData): T {
    if(isNullOrUndefined(DataModelFactory.instance)) {
      throw new SerializationException('Data model factory is not initialized.');
    }

    return DataModelFactory.instance.fromData(this, map) as T;
  }

  fromDataOrNull<T extends DataModel>(map: SerializedData | null): T | null {
    return isNullOrUndefined(map) ? null : this.fromData<T>(map);
  }

  fromDataOrNullOrUndefined<T extends DataModel>(map: SerializedData | null | undefined): T | null | undefined {
    return isNullOrUndefined(map) ? map : this.fromData<T>(map);
  }

  encodeDate(date: Dayjs): number {
    return TimeZone.encodeDate(date);
  }

  encodeDateOrNull(date: Dayjs | null): number | null {
    return TimeZone.encodeDateOrNull(date);
  }

  decodeDate(encodedValue: number): Dayjs {
    return TimeZone.decodeDate(encodedValue);
  }

  decodeDateOrNull(encodedValue: number | null): Dayjs | null {
    return TimeZone.decodeDateOrNull(encodedValue);
  }

  encodeDuration(duration: Duration): number {
    return TimeZone.encodeDuration(duration);
  }

  encodeDurationOrNull(duration: Duration | null): number | null {
    return TimeZone.encodeDurationOrNull(duration);
  }

  decodeDuration(encodedValue: number): Duration {
    return TimeZone.decodeDuration(encodedValue);
  }

  decodeDurationOrNull(encodedValue: number | null): Duration | null {
    return TimeZone.decodeDurationOrNull(encodedValue);
  }
}

export function createSerializationContext(): SerializationContext {
  return new SerializationContextImpl();
}
